/**
 * Location Page V2 item 4 — the job page's §6d Water & Reporting card,
 * site-scoped: the jurisdiction facts (purveyor, portal, purveyor account #,
 * report form) live on the site itself, not only on jobs that visit it.
 *
 * Editing goes through the ONE shared Water & Reporting editor
 * (`WaterReportingEditorDialog`) — same component the job page and the
 * customer Locations dialog mount. The portal account is the canonical
 * `sites.water_account_number`; the legacy ST code (`sites.account_number`)
 * shows only as a muted reference line, never as the portal account.
 */
import { useState } from "react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { WaterReportingEditorDialog } from "@/components/water-reporting-editor";
import { openPriceCheck } from "@/components/price-check";
import { useAuth } from "@/lib/auth";
import { PERMISSIONS } from "@/lib/permissions";
import { DollarSign, Droplet, ExternalLink, Pencil } from "lucide-react";
import { openExternalLink } from "@/lib/standalone";

export interface SiteWaterFacts {
    id: string;
    customer_id: string;
    water_provider_id?: string | null;
    water_provider_name: string | null;
    water_provider_display_name?: string | null;
    water_provider_submission_platform: string | null;
    water_provider_is_portal?: boolean | null;
    water_provider_portal_url: string | null;
    water_provider_report_name?: string | null;
    water_account_number?: string | null;
    account_number: string | null;
}

export function SiteWaterReportingCard({ site }: { site: SiteWaterFacts }) {
    const [editing, setEditing] = useState(false);
    const { can } = useAuth();
    const editDialog = editing ? (
        <WaterReportingEditorDialog siteId={site.id} customerId={site.customer_id} onClose={() => setEditing(false)} />
    ) : null;

    if (!site.water_provider_name) {
        // Location Page V2 gap, closed: the site page can now SET the
        // purveyor, not just complain about the missing one.
        return (
            <Card className="border-sky-200/60 dark:border-sky-500/20">
                <CardContent className="p-4 flex items-center justify-between gap-2 text-sm text-foreground/70">
                    <span className="flex items-center gap-2">
                        <Droplet className="h-3.5 w-3.5 text-sky-600 dark:text-sky-400" />
                        No water purveyor set.
                    </span>
                    <Button variant="outline" size="sm" onClick={() => setEditing(true)}>
                        <Pencil className="mr-1.5 h-3.5 w-3.5" /> Set purveyor
                    </Button>
                </CardContent>
                {editDialog}
            </Card>
        );
    }

    const platformBadge = site.water_provider_submission_platform
        ? `${site.water_provider_is_portal ? "Portal" : "Reports"}: ${site.water_provider_submission_platform}`
        : "Reports: Paper / manual";
    const method = site.water_provider_is_portal
        ? "Results entered in the purveyor's online portal"
        : site.water_provider_submission_platform
          ? `Reports filed via ${site.water_provider_submission_platform}`
          : "Reports filed on paper / mailed";
    const portalAccount = site.water_account_number?.trim() || null;
    const legacyCode = site.account_number?.trim() || null;

    return (
        <Card>
            <CardContent className="p-5 space-y-3">
                <div className="flex items-center justify-between">
                    <h2 className="text-sm font-semibold flex items-center gap-2">
                        <Droplet className="h-4 w-4 text-sky-600 dark:text-sky-400" /> Water &amp; Reporting
                    </h2>
                    <span className="flex items-center gap-1">
                        {/* PRICE CHECK from the site page — opens straight to
                            this site's pinned provider. Office only. */}
                        {site.water_provider_id && can(PERMISSIONS.pricing.view) && (
                            <Button
                                variant="ghost"
                                size="sm"
                                className="h-7 px-2 text-xs"
                                title="Price check — what a backflow test costs here"
                                onClick={() =>
                                    openPriceCheck({
                                        providerId: site.water_provider_id!,
                                        providerName:
                                            site.water_provider_display_name || site.water_provider_name || undefined,
                                    })
                                }
                            >
                                <DollarSign className="mr-1 h-3.5 w-3.5" /> Price check
                            </Button>
                        )}
                        <Button
                            variant="ghost"
                            size="sm"
                            className="h-7 px-2 text-xs"
                            title="Edit jurisdiction, portal account, and on-site contact"
                            onClick={() => setEditing(true)}
                        >
                            <Pencil className="mr-1 h-3.5 w-3.5" /> Edit
                        </Button>
                    </span>
                </div>
                <div className="space-y-2 rounded-md border border-l-4 border-l-sky-500 bg-sky-50 p-3 dark:border-l-sky-400 dark:bg-sky-950">
                    <div className="text-base font-bold leading-tight text-foreground">
                        {site.water_provider_display_name || site.water_provider_name}
                    </div>
                    <div className="flex flex-wrap items-center gap-x-2 gap-y-1.5">
                        <Badge className="border-transparent bg-sky-700 text-white">{platformBadge}</Badge>
                        {site.water_provider_portal_url && (
                            <Button
                                variant="outline"
                                size="sm"
                                onClick={() => openExternalLink(site.water_provider_portal_url!)}
                            >
                                <ExternalLink className="mr-1.5 h-3.5 w-3.5" /> Open portal
                            </Button>
                        )}
                    </div>
                    {/* Portal account is portal-only. Paper/offline jurisdictions
                        file by mail — a number in `water_account_number` there is
                        stale/legacy and routes nowhere, so hide the whole account
                        block (mirrors the editor's "reports offline — no account
                        number needed"). The method line below still says paper. */}
                    {site.water_provider_is_portal && (
                        <>
                            <div className="flex items-center gap-1.5 text-sm text-foreground">
                                <span className="text-foreground/70">Account #:</span>
                                {portalAccount ? (
                                    <span className="font-mono">{portalAccount}</span>
                                ) : (
                                    <button
                                        type="button"
                                        className="font-medium text-sky-700 hover:underline dark:text-sky-300"
                                        onClick={() => setEditing(true)}
                                    >
                                        + Add account #
                                    </button>
                                )}
                            </div>
                            {!portalAccount && legacyCode && (
                                <p className="text-xs text-foreground/60">
                                    Legacy ST code: <span className="font-mono">{legacyCode}</span> (not the portal
                                    account)
                                </p>
                            )}
                        </>
                    )}
                    <p className="text-sm text-foreground">{method}</p>
                    {site.water_provider_report_name && (
                        <p className="text-sm text-foreground">Report form: {site.water_provider_report_name}</p>
                    )}
                </div>
            </CardContent>
            {editDialog}
        </Card>
    );
}
